/**
 * Tenant Documents — Cutover to <LibraryDocumentsView scope="tenant">.
 *
 * Pre-cutover this file was 1063 LOC of inline table + filter row + bulk
 * bar + row dropdowns + sidebar + viewer + editor + 4 dialogs. The
 * unified table now lives in the shared component (Library-PR-X3 /
 * Phase 4) — this route shell owns the two full-page modes
 * (list / edit) and the dialogs that wrap the list.
 *
 * Founder-locked cutover scope (12 affordances + 1 deferred):
 *   Path B (wired INTO the View body — see LibraryDocumentsView.tsx):
 *     #2/#3 Star column + handlers
 *     #7    Share-doc dropdown row item
 *     #10   Bulk Move + Bulk Delete buttons (multi-action bar)
 *     #12   Sortable column headers (the server orders the list)
 *     #13   Pagination ("Show more (N remaining)" asks the server)
 *   Path C (composed AROUND the View — owned by this file):
 *     #1  KbSidebar
 *     #4  (KbViewer read mode — deleted, decision 43: nothing ever entered
 *         it; an article is read at /library/article/$articleId)
 *     #5  WikiEditor (edit mode)
 *     #6  "Types" and "Tags" buttons (passed to View via headerActions slot)
 *     #8  Share-folder dialog (triggered from KbSidebar.onShareFolder)
 *     #9  Move-to-folder dialog (View fires onBulkMove → route opens dialog)
 *   Deferred:
 *     #11 Bulk Tag stub — dropped entirely.
 *
 * THE SERVER ANSWERS (Library LB6-T2, acceptance L6-15 .. L6-18). This page
 * holds NO copy of a list: no documents, articles or folders in route state.
 * The table is the View's query (GET /knowledge/documents with every filter);
 * the sidebar's folders and badges are the one folder query (each folder's
 * `count`); "All Documents" is the documents route's total; the pages in a
 * folder are that folder's GET /wiki/articles. Every write calls
 * `invalidateLibraryLists`, so all of them re-ask the server at once. A
 * failed read is an error wherever it is shown — never an empty list, never
 * a 0. An article being written is the editor's alone until it is saved: it
 * is not a row and bumps no badge. Every delete — table, sidebar, editor —
 * and every unpublish is confirmed in an in-app dialog.
 *
 * The list-mode shell is the View. The edit mode owns the full
 * content area (no sidebar overlap; sidebar stays mounted so the user
 * can switch folders without losing the editor state — matches
 * pre-cutover behaviour).
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Settings, Loader2, Folder as FolderIcon, Send,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { apiClient } from '@/lib/api-client';
import { getApiErrorMessage } from '@/lib/api-error';
import { toast } from 'sonner';
import { useHasPermission } from '@/lib/use-has-permission';
import { useAuth } from '@/contexts/auth-context';
import { useLibraryFolders, type LibraryFolder } from '@/lib/use-library-folders';
import {
  invalidateLibraryLists,
  useLibraryDocumentTotal,
  useWikiTags,
  wikiTagsQueryKey,
} from '@/lib/use-library-documents';
import { FolderListError } from '@/components/library/folder-picker-states';

import { KbSidebar, type KBFolder } from '@/components/kb/KbSidebar';
import { TagManager, type KBTag } from '@/components/kb/TagManager';
import { WikiEditor, type KBArticle } from '@/components/kb/WikiEditor';
import { CreateFolderDialog } from '@/components/kb/CreateFolderDialog';
import { UploadDialog } from '@/components/library/upload-dialog';
import { ManageTypesDialog } from '@/components/library/manage-types-dialog';
import { ShareDocumentDialog } from '@/components/library/ShareDocumentDialog';
import { ShareFolderDialog } from '@/components/library/ShareFolderDialog';
import { articleForEditor } from '@/lib/article-for-editor';
import type { WikiArticleDetail } from '@/types/library';

import {
  DeleteConfirmDialog,
  LibraryDocumentsView,
  buildArticleDeleteIntent,
  useTenantAdapter,
  type ArticleReaderContext,
  type DeleteIntent,
  type LibraryDocumentsAdapter,
  type NormalizedLibraryDoc,
} from '@/components/library-documents-shared';

// Decision 19 (LB3-T5) — every row is a document with its stored kind: an
// uploaded file, a wiki article, or a document a library connector synced.
// The list shows real rows only (W2-H-02, no mock data in prod).

// #236 — TanStack Router strips unrecognised search params unless the
// route declares them. The `/library/wiki/new` redirect (PR #233)
// passes `?action=article` to land users in the New Article flow,
// and `/library/article/:id`'s Edit button passes `?edit=<id>` to
// open the editor directly. Declaring the keys keeps them in the URL
// so the post-load handler can read them.
interface DocumentsSearch {
  action?: 'article' | 'upload';
  edit?: string;
}

const PENDING_NEW_ARTICLE_KEY = 'argusai.library.pendingNewArticle';

function hasPendingNewArticleAction(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(PENDING_NEW_ARTICLE_KEY) === '1';
  } catch {
    return false;
  }
}

function clearPendingNewArticleAction(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.removeItem(PENDING_NEW_ARTICLE_KEY);
  } catch {
    // Ignore storage-denied browsers; the URL action path still works.
  }
}

/**
 * A deep link (?action=upload, ?action=article, ?edit=<id>) is acted on ONCE,
 * and only once the person's grants have loaded (LB6-T3 review BF-01).
 *
 * The page opens the editor or the upload dialog only for a person who holds
 * the key (`useHasPermission`), and that answer comes from GET /users/me. Until
 * it lands the grant set is EMPTY (auth-context DEFAULT_USER), so a cold load
 * of a bookmarked /library/wiki/new asked "may write?" of nobody, was told no,
 * marked the link handled — and when the grants arrived a moment later the
 * link was never looked at again: a false "no permission" toast and no
 * editor. So: while the grants load the answer is 'wait' and the link is NOT
 * consumed; afterwards it is 'claimed' exactly once ('handled' every time
 * after).
 */
export function claimDeepLink(
  handled: { current: string | null },
  link: string,
  grantsLoading: boolean,
): 'wait' | 'handled' | 'claimed' {
  if (grantsLoading) return 'wait';
  if (handled.current === link) return 'handled';
  handled.current = link;
  return 'claimed';
}

export const Route = createFileRoute('/library/documents')({
  validateSearch: (search: Record<string, unknown>): DocumentsSearch => {
    const action = search.action;
    const edit = search.edit;
    return {
      action: action === 'article' || action === 'upload' ? action : undefined,
      edit: typeof edit === 'string' && edit.length > 0 ? edit : undefined,
    };
  },
  component: DocumentsPage,
});

// ─── The list, with the Types and Tags buttons ──────────────────────────────

/**
 * "Types" — opens Manage document types (POST / DELETE /knowledge/document-types,
 * write:library.documents, OQ-L6-5). Drawn only for a person who holds that
 * key (L6-04).
 */
function ManageTypesButton({ onClick }: { onClick: () => void }) {
  const canManageTypes = useHasPermission('write', 'library.documents');
  if (!canManageTypes) return null;
  return (
    <Button
      variant="outline"
      size="sm"
      className="gap-1 text-xs"
      onClick={onClick}
      title="Manage document types"
    >
      <Settings className="h-3 w-3" /> Types
    </Button>
  );
}

/**
 * The page in list mode: the shared list, with the page's two catalogue
 * buttons beside New — "Types" (custom document types) and "Tags" (the tag
 * catalogue, the one TagManager the article editor draws too). Both write
 * through routes that declare write:library.documents (POST / DELETE
 * /knowledge/document-types; POST / DELETE /wiki/tags — decision 26), and
 * each draws itself only for a person who holds that key.
 *
 * Tags were managed ONLY inside the article editor, which opens with
 * write:wiki: a Member (write:library.documents, no write:wiki) was allowed by
 * the route and had no control to reach it (LB-6 run 1 live proof). Exported
 * so the per-role control test renders the page's list, not a control alone.
 */
export function DocumentsListPane({
  adapter, activeFolder, onManageTypes, tags, tagDocumentCounts, onTagsChange,
}: {
  adapter: LibraryDocumentsAdapter;
  activeFolder: KBFolder | null;
  onManageTypes: () => void;
  tags: KBTag[];
  tagDocumentCounts: Record<string, number>;
  onTagsChange: () => void;
}) {
  // #676 — Sidebar selection filters the table: the active folder id is the
  // View's `folderFilterOverride`, which the list sends to the server as
  // `folderId` (LB6-T2: the table is the server's answer). `undefined` means
  // no override — the user controls the in-header Select.
  return (
    <LibraryDocumentsView
      scope="tenant"
      adapter={adapter}
      pageTitleOverride={activeFolder?.name}
      folderFilterOverride={activeFolder ? activeFolder.id : undefined}
      headerActions={
        <>
          <ManageTypesButton onClick={onManageTypes} />
          <TagManager
            trigger="button"
            tags={tags}
            onTagsChange={onTagsChange}
            documentCounts={tagDocumentCounts}
          />
        </>
      }
    />
  );
}

// ─── The unpublish confirmation ────────────────────────────────────────────

/**
 * "Unpublish …?" — an in-app dialog (Library LB6-T2, L6-18: no native
 * `window.confirm` in the Library). Exported for the confirm test.
 */
export function UnpublishConfirmDialog({
  article, busy, onCancel, onConfirm,
}: {
  article: { id: string; title: string } | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <AlertDialog open={article !== null} onOpenChange={(open) => { if (!open && !busy) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Unpublish &ldquo;{article?.title}&rdquo;?</AlertDialogTitle>
          <AlertDialogDescription>
            It will return to draft and be removed from the RAG index.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-unpublish"
            disabled={busy}
            onClick={(e) => {
              // Keep the dialog open until the request answers.
              e.preventDefault();
              onConfirm();
            }}
          >
            {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : null}
            Unpublish
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * THE unpublish the Library list asks for, once its dialog is confirmed
 * (Library LB6-T6, L6-18). It goes through the server's ONE unpublish path:
 * PUT /wiki/articles/:id with `status: 'draft'` — the article edit route,
 * which moves a published article through the Document writer's
 * `unpublishArticle` (it removes the article's search copies, so it is gone
 * from Ask Argus at once). It was a PATCH, a verb no /wiki route answers:
 * every "Unpublish…" was a 404 (since #463). The list re-asks the server
 * before success is shown; a refusal is an error toast and the dialog stays.
 * Resolves true when the article is unpublished. Exported for the verb test.
 */
export async function unpublishArticleRow(
  target: { id: string; title: string },
  refreshLists: () => Promise<unknown>,
): Promise<boolean> {
  try {
    await apiClient.put(`/wiki/articles/${encodeURIComponent(target.id)}`, { status: 'draft' });
  } catch (err: unknown) {
    toast.error(getApiErrorMessage(err, 'Unpublish failed'));
    return false;
  }
  await refreshLists();
  toast.success(`Unpublished "${target.title}"`);
  return true;
}

// ─── Page ───────────────────────────────────────────────────────────────────

function DocumentsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Library LB6-T3 / L6-04 — each control asks EXACTLY the key its route
  // declares (LIBRARY_CONTROLS in @argusai/shared). The list's controls are
  // the tenant adapter's, the sidebar's and the Types and Tags buttons' own; this page
  // opens the article editor (new, ?edit=, ?action=article) only for
  // write:wiki — its Save is POST / PUT /wiki/articles — and the upload
  // dialog (?action=upload) only for write:library.documents — its Upload is
  // POST /knowledge/documents/upload.
  const canWriteArticles = useHasPermission('write', 'wiki');
  const canUpload = useHasPermission('write', 'library.documents');
  // Those two answers are only real once GET /users/me has landed: the deep
  // links below wait for it (claimDeepLink, review BF-01).
  const { isLoading: grantsLoading } = useAuth();

  // Deep-link search params (#236 / LIB-03).
  //
  // #638 LIB-ROUTE-009 / LIB-WIKI-002 — pre-fix this read
  // `window.location.search` at first render. When /library/wiki/new
  // redirects here via `beforeLoad`, the destination component can mount
  // before TanStack syncs history, so `urlParams.get('action')` returned
  // null and the editor branch never fired. The route shim now uses a raw
  // `/library/documents?action=article` href (#1337), and this page
  // consumes the validated search object instead of `window.location`.
  const search = Route.useSearch();
  const actionFromUrl = search.action ?? (hasPendingNewArticleAction() ? 'article' : null);
  const editFromUrl = search.edit ?? null;
  const handledActionRef = useRef<string | null>(null);
  const handledEditRef = useRef<string | null>(null);

  // Data — the server's answers, read where they are shown (LB6-T2).
  //
  // Decision 18 / LB2-T3-R2 — the folders are the ONE folder query
  // (lib/use-library-folders.ts), observed directly. The sidebar (with each
  // folder's `count` as its badge), the Move dialog, the list, the upload
  // dialog and every other reader on this page show the same read, and a
  // write's invalidation reaches them all: there is no private copy to
  // hand-sync.
  const foldersQ = useLibraryFolders();
  const folders: LibraryFolder[] = useMemo(() => foldersQ.data ?? [], [foldersQ.data]);
  // "All Documents" — the server's total for every kind, no folder.
  const totalQ = useLibraryDocumentTotal();
  // The tags, each with how many documents this person may see carry it —
  // the server's count (GET /wiki/tags), not a tally of loaded articles — for
  // the header's tag manager. The editor's tag field reads the same query key
  // itself (components/library/document-tags-field.tsx, LB6c-T1).
  const tagsQ = useWikiTags();
  const tags = useMemo(() => tagsQ.data ?? [], [tagsQ.data]);
  const tagCounts = useMemo(
    () => Object.fromEntries(tags.map((t) => [t.id, t.count])) as Record<string, number>,
    [tags],
  );
  // The page is loading until the first folder read settles (a failed folder
  // read settles too: it is shown as an error, below).
  const loading = foldersQ.isLoading;

  // UI State
  const [activeFolder, setActiveFolder] = useState<KBFolder | null>(null);
  // The article open in the editor — a saved one (read from GET
  // /wiki/articles/:id) or a new one not yet saved, which is in NO list.
  const [activeDoc, setActiveDoc] = useState<KBArticle | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'edit'>('list');

  // Dialogs
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showManageTypes, setShowManageTypes] = useState(false);
  const [shareDoc, setShareDoc] = useState<{ id: string; title: string } | null>(null);
  const [shareFolder, setShareFolder] = useState<{ id: string; name: string } | null>(null);
  // #4 — folder delete now flows through the app's styled AlertDialog
  // (matching the document-delete UX inside LibraryDocumentsView) rather
  // than a native browser confirm. `pendingFolderDelete` holds the folder
  // being confirmed; the confirm handler performs the DELETE.
  const [pendingFolderDelete, setPendingFolderDelete] = useState<{ id: string; name: string } | null>(null);
  const [folderDeleteBusy, setFolderDeleteBusy] = useState(false);
  // L6-18 — an article deleted from the sidebar or the editor is confirmed in
  // the SAME dialog the table's row Delete opens (DeleteConfirmDialog, with
  // buildArticleDeleteIntent).
  const [pendingArticleDelete, setPendingArticleDelete] = useState<DeleteIntent | null>(null);
  // L6-18 — Unpublish is confirmed in an in-app dialog.
  const [pendingUnpublish, setPendingUnpublish] = useState<{ id: string; title: string } | null>(null);
  const [unpublishBusy, setUnpublishBusy] = useState(false);

  // Bulk + row move dialog (Path C #9 — route owns the state)
  const [moveToFolder, setMoveToFolder] = useState<{
    targetIds: string[];
    title?: string;
  } | null>(null);
  const [moveToFolderId, setMoveToFolderId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  // ── Data loading ──────────────────────────────────────────────────────────

  // LB2T3-BF-03 — a failed folder read is an error, said once, never an empty
  // sidebar and Move dialog. (The list on this page shows it too: it reads the
  // same query, LibraryDocumentsView.adapters.tsx.)
  useEffect(() => {
    if (foldersQ.isError && !foldersQ.data) {
      toast.error('The folder list could not be loaded.', { id: 'library-folders-error' });
    }
  }, [foldersQ.isError, foldersQ.data]);

  /** Every Library list re-asks the server (L6-18). */
  const refreshLists = useCallback(() => invalidateLibraryLists(queryClient), [queryClient]);

  // The tag catalogue changed (the page's Tags button or the editor's): the
  // one tag query re-asks the server (LB6-T2) — its counts come back with it.
  const refreshTags = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: wikiTagsQueryKey });
  }, [queryClient]);

  const handleUploadComplete = useCallback(async () => {
    await refreshLists();
  }, [refreshLists]);

  // ── Article creation helper (used by URL deep-link + adapter) ─────────────

  const handleNewArticle = useCallback((parentId?: string, folderId?: string) => {
    // The editor saves through POST /wiki/articles (write:wiki): a person
    // without the key is not handed an editor whose Save would be refused.
    if (!canWriteArticles) {
      toast.error('You do not have permission to write articles.');
      return;
    }
    // Issue #89 — backend POST /wiki/articles requires a non-empty
    // folderId. When no active folder is selected, fall back to the
    // first folder so the editor opens with a valid default.
    const fallbackFolderId = folderId ?? activeFolder?.id ?? folders[0]?.id ?? null;
    // The new article is the editor's alone until it is saved: it is not
    // added to any list and changes no badge (L6-15).
    const newDoc: KBArticle = {
      id: `draft-${Date.now()}`,
      title: 'Untitled Article',
      content: '<p></p>',
      status: 'draft',
      folderId: fallbackFolderId,
      parentId: parentId ?? null,
      tags: [],
      authorName: 'You',
      viewCount: 0,
      version: 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      publishedAt: null,
    };
    setActiveDoc(newDoc);
    setViewMode('edit');
  }, [activeFolder, folders, canWriteArticles]);

  // Handle ?action= from URL search params.
  //
  // #983 — the editor opens as soon as there is an article action: it only
  // needs a new article and a fallback folder, both of which exist without
  // waiting for any list. The render path below also bypasses the spinner
  // when we have an active edit session.
  useEffect(() => {
    if (!actionFromUrl) return;
    if (actionFromUrl === 'upload') {
      // The upload dialog's folder picker reads the folder list: wait for it.
      if (!loading) {
        if (claimDeepLink(handledActionRef, actionFromUrl, grantsLoading) !== 'claimed') return;
        if (canUpload) setShowUpload(true);
        else toast.error('You do not have permission to upload documents.');
        window.history.replaceState({}, '', window.location.pathname);
      }
      return;
    }
    if (actionFromUrl === 'article') {
      const claim = claimDeepLink(handledActionRef, actionFromUrl, grantsLoading);
      if (claim === 'wait') return;
      clearPendingNewArticleAction();
      if (claim === 'handled') return;
      // Open the editor IMMEDIATELY. Folder lists may still be loading;
      // handleNewArticle falls back to `null` parent + first-available
      // folder.
      handleNewArticle();
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [loading, actionFromUrl, handleNewArticle, canUpload, grantsLoading]);

  // Handle ?edit=<id>
  //
  // BUG-686 / BUG-687 — the editor opens on the article's full detail (GET
  // /wiki/articles/:id, body included). If the read fails (or answers no
  // body) the editor does NOT open: saving an empty buffer would overwrite
  // the stored body (LB3T5-BF-02). The person is told why and stays on the
  // list. LB6-T2 (L6-16): the detail read is the ONLY read — the link no
  // longer looks the article up in a list it loaded, so an article past that
  // list's first page opens like any other.
  useEffect(() => {
    if (!editFromUrl) return;
    if (claimDeepLink(handledEditRef, editFromUrl, grantsLoading) !== 'claimed') return;
    // ?edit=<id> opens the editor, whose Save is PUT /wiki/articles/:id
    // (write:wiki): never for a person who may not save.
    if (!canWriteArticles) {
      toast.error('You do not have permission to edit articles.');
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    let cancelled = false;
    (async () => {
      let opened: KBArticle | null = null;
      let failure: unknown = null;
      try {
        const fresh = await apiClient.get<WikiArticleDetail>(`/wiki/articles/${encodeURIComponent(editFromUrl)}`);
        opened = articleForEditor(fresh);
      } catch (err) {
        failure = err;
      }
      if (cancelled) return;
      window.history.replaceState({}, '', window.location.pathname);
      if (!opened) {
        toast.error(getApiErrorMessage(failure, 'The article could not be loaded for editing. Nothing was changed.'));
        return;
      }
      setActiveDoc(opened);
      setViewMode('edit');
    })();
    return () => { cancelled = true; };
  }, [editFromUrl, canWriteArticles, grantsLoading]);

  // ── Bulk + row move handlers (Path C #9) ──────────────────────────────────

  const openMoveDialog = useCallback((targetIds: string[], title?: string, currentFolderId?: string | null) => {
    setMoveToFolder({ targetIds, title });
    setMoveToFolderId(currentFolderId ?? null);
  }, []);

  const closeMoveDialog = useCallback(() => {
    setMoveToFolder(null);
    setMoveToFolderId(null);
  }, []);

  const handleConfirmMove = useCallback(async () => {
    if (!moveToFolder) return;
    setBulkBusy(true);
    try {
      const res = await apiClient.post<{
        success: boolean;
        applied: number;
        skipped: { id: string; reason: string }[];
      }>('/knowledge/documents/bulk', {
        action: 'move',
        ids: moveToFolder.targetIds,
        folderId: moveToFolderId,
      });
      await refreshLists();
      if (res.applied > 0) {
        const dest = folders.find(f => f.id === moveToFolderId)?.name ?? 'root';
        toast.success(`Moved ${res.applied} document${res.applied === 1 ? '' : 's'} to ${dest}.`);
      }
      if (res.skipped.length > 0) {
        toast.warning(`${res.skipped.length} item${res.skipped.length === 1 ? '' : 's'} skipped (wiki articles or missing).`);
      }
      closeMoveDialog();
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Move failed'));
    } finally {
      setBulkBusy(false);
    }
  }, [moveToFolder, moveToFolderId, folders, closeMoveDialog, refreshLists]);

  const handleBulkDelete = useCallback(async (
    selectedIds: Set<string>,
    selectedDocs: NormalizedLibraryDoc[],
  ) => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    // #679 — the user has already confirmed in the View's in-app dialog.
    setBulkBusy(true);
    try {
      const res = await apiClient.post<{
        success: boolean;
        applied: number;
        skipped: { id: string; reason: string }[];
      }>('/knowledge/documents/bulk', { action: 'delete', ids });
      await refreshLists();
      if (res.applied > 0) toast.success(`Deleted ${res.applied} document${res.applied === 1 ? '' : 's'}.`);
      if (res.skipped.length > 0) toast.warning(`${res.skipped.length} skipped.`);
      void selectedDocs;
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Bulk delete failed'));
    } finally {
      setBulkBusy(false);
    }
  }, [refreshLists]);

  // ── Per-row handlers (forwarded to adapter) ───────────────────────────────

  const handleSelectArticle = useCallback((articleId: string, context?: ArticleReaderContext) => {
    // #144 — Per the Library UI spec, clicking a wiki item navigates to
    // the dedicated article reader route. The list it was opened from rides
    // along, so the reader's Previous / Next follow that list (L6-16).
    navigate({
      to: '/library/article/$articleId',
      params: { articleId },
      search: context ?? {},
    });
  }, [navigate]);

  /** THE article delete — after the in-app confirmation, wherever it was asked from. */
  const handleDeleteArticle = useCallback(async (id: string) => {
    try {
      await apiClient.delete(`/wiki/articles/${id}`);
      await refreshLists();
      if (activeDoc?.id === id) {
        setActiveDoc(null);
        setViewMode('list');
      }
      toast.success('Article deleted');
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to delete'));
    }
  }, [activeDoc, refreshLists]);

  const handleDeleteUploaded = useCallback(async (id: string) => {
    // #677 — the user has already confirmed in the View's in-app dialog.
    try {
      await apiClient.delete(`/knowledge/documents/${id}`);
      await refreshLists();
      toast.success('Document deleted');
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Failed to delete'));
    }
  }, [refreshLists]);

  // #712 — Row-level View + Download for uploaded docs (Pod-DDD cluster).
  const handleViewUploaded = useCallback((doc: NormalizedLibraryDoc) => {
    navigate({ to: '/library/doc/$docId', params: { docId: doc.id } });
  }, [navigate]);

  const handleDownloadUploaded = useCallback(async (doc: NormalizedLibraryDoc) => {
    try {
      const { downloadFile } = await import('@/lib/upload-utils');
      await downloadFile(
        `/knowledge/documents/${doc.id}/file`,
        doc.fileName ?? doc.title,
      );
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Download failed'));
    }
  }, []);

  // ── Publish / Unpublish (P1 #1, Round 1 Library audit 2026-05-13) ────────
  //
  // The shared View body fires onPublishRow / onUnpublishRow for tenant
  // articles whose state is `draft` or `published`. After the server answers
  // the lists re-ask it, so the row shows the new state without a reload.

  const handlePublishArticle = useCallback(async (doc: NormalizedLibraryDoc) => {
    try {
      await apiClient.post<{ success: boolean; status: string }>(
        `/wiki/articles/${doc.id}/publish`,
      );
      await refreshLists();
      toast.success(`Published "${doc.title}"`);
    } catch (err: unknown) {
      toast.error(getApiErrorMessage(err, 'Publish failed'));
    }
  }, [refreshLists]);

  /** Unpublish… opens the in-app confirmation; the request is sent on Confirm. */
  const handleUnpublishArticle = useCallback((doc: NormalizedLibraryDoc) => {
    setPendingUnpublish({ id: doc.id, title: doc.title });
  }, []);

  const confirmUnpublish = useCallback(async () => {
    const target = pendingUnpublish;
    if (!target) return;
    setUnpublishBusy(true);
    try {
      // The dialog closes only once the server has unpublished it.
      if (await unpublishArticleRow(target, refreshLists)) setPendingUnpublish(null);
    } finally {
      setUnpublishBusy(false);
    }
  }, [pendingUnpublish, refreshLists]);

  // ── Adapter (memoised inside the hook) ────────────────────────────────────

  const adapter = useTenantAdapter({
    onOpenUpload: () => setShowUpload(true),
    onOpenCreateArticle: () => handleNewArticle(),
    onOpenCreateFolder: () => setShowCreateFolder(true),
    onSelectArticle: handleSelectArticle,
    onOpenUploadedDoc: (id) => navigate({ to: '/library/doc/$docId', params: { docId: id } }),
    onDeleteArticle: handleDeleteArticle,
    onDeleteUploaded: handleDeleteUploaded,
    onBulkMove: (selectedIds, selectedDocs) => {
      openMoveDialog(
        Array.from(selectedIds),
        selectedDocs.length === 1 ? selectedDocs[0]?.title : undefined,
      );
    },
    onBulkDelete: handleBulkDelete,
    bulkBusy,
    onShareDocument: (doc) => setShareDoc({ id: doc.id, title: doc.title }),
    onRowMove: (doc) => openMoveDialog([doc.id], doc.title, doc.folderId),
    onPublishArticle: handlePublishArticle,
    onUnpublishArticle: handleUnpublishArticle,
    onViewUploaded: handleViewUploaded,
    onDownloadUploaded: handleDownloadUploaded,
  });

  // ── Edit mode helpers ─────────────────────────────────────────────────────

  const handleBack = () => {
    setActiveDoc(null);
    setViewMode('list');
  };

  // ── Render ────────────────────────────────────────────────────────────────

  // #983 — only block on the spinner for the LIST view. The /library/wiki/new
  // deep-link sets viewMode='edit' on first paint via the action effect
  // above; the edit mode is self-contained (works off `activeDoc`), so it is
  // safe to render while the folder list is still in flight.
  if (loading && viewMode === 'list') {
    return (
      <div
        className="flex items-center justify-center h-full"
        aria-busy="true"
        aria-live="polite"
        data-testid="library-documents-loading"
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Sidebar (Path C #1) — always mounted so the user can switch
          folders without losing edit state */}
      <KbSidebar
        folders={folders}
        foldersFailed={foldersQ.isError && !foldersQ.data ? { onRetry: () => void foldersQ.refetch() } : null}
        activeFolder={activeFolder}
        activeDocId={activeDoc?.id ?? null}
        totalDocCount={totalQ.isError ? null : totalQ.data ?? null}
        onSelectFolder={f => {
          setActiveFolder(f);
          setViewMode('list');
          setActiveDoc(null);
        }}
        // The page's OWN folder (the tree it was clicked in), never the active
        // folder: another open folder's page is clickable too (LB6T2-BUG-01).
        onSelectDoc={(id, folderId) => handleSelectArticle(id, { folderId })}
        onCreateFolder={() => setShowCreateFolder(true)}
        onCreateDoc={(parentId, folderId) => handleNewArticle(parentId ?? undefined, folderId ?? undefined)}
        onDeleteDoc={(id, title) => setPendingArticleDelete(buildArticleDeleteIntent({ id, title }))}
        onDeleteFolder={(id) => {
          const folder = folders.find(f => f.id === id);
          // #4 — open the styled AlertDialog instead of native confirm.
          // The actual DELETE happens in the dialog's confirm handler.
          setPendingFolderDelete({ id, name: folder?.name ?? id });
        }}
        onShareFolder={(id, name) => setShareFolder({ id, name })}
      />

      {/* Content Area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* List Mode → shared LibraryDocumentsView, with the Types and
            Tags buttons beside New (#6 Path C) */}
        {viewMode === 'list' && (
          <div className="flex-1 overflow-auto">
            <DocumentsListPane
              adapter={adapter}
              activeFolder={activeFolder}
              onManageTypes={() => setShowManageTypes(true)}
              tags={tags}
              tagDocumentCounts={tagCounts}
              onTagsChange={refreshTags}
            />
          </div>
        )}

        {/* Edit Mode — Wiki Editor (Path C #5) */}
        {viewMode === 'edit' && activeDoc && (
          <WikiEditor
            doc={activeDoc}
            folders={folders}
            onBack={handleBack}
            onSave={async (updatedDoc) => {
              setActiveDoc(updatedDoc);
              await refreshLists();
            }}
            onDelete={async (id) => {
              // A new article that was never saved exists nowhere: closing
              // the editor is all there is to delete. A saved one is
              // confirmed in the one article delete dialog (L6-18).
              if (id.startsWith('draft-')) {
                setActiveDoc(null);
                setViewMode('list');
                return;
              }
              setPendingArticleDelete(buildArticleDeleteIntent({ id, title: activeDoc.title }));
            }}
            onFolderChange={async (docId, folderId) => {
              // The editor saves the folder it is handed: the open article
              // takes the new folder (it is saved with the next Save).
              setActiveDoc(prev => (prev && prev.id === docId ? { ...prev, folderId } : prev));
            }}
          />
        )}
      </div>

      {/* Dialogs (Path C) */}
      <CreateFolderDialog
        open={showCreateFolder}
        onOpenChange={setShowCreateFolder}
        // CreateFolderDialog invalidates the one folder query itself.
        onCreated={() => undefined}
      />
      <UploadDialog
        open={showUpload}
        onOpenChange={setShowUpload}
        onUploaded={handleUploadComplete}
        defaultFolderId={activeFolder?.id}
      />
      <ManageTypesDialog open={showManageTypes} onOpenChange={setShowManageTypes} />

      {/* L6-18 — the article delete asked from the sidebar or the editor is
          confirmed in the same dialog as the table's row Delete. */}
      <DeleteConfirmDialog
        intent={pendingArticleDelete}
        onCancel={() => setPendingArticleDelete(null)}
        onConfirm={(intent) => {
          setPendingArticleDelete(null);
          const id = intent.ids[0];
          if (id) void handleDeleteArticle(id);
        }}
      />

      <UnpublishConfirmDialog
        article={pendingUnpublish}
        busy={unpublishBusy}
        onCancel={() => setPendingUnpublish(null)}
        onConfirm={() => void confirmUnpublish()}
      />

      {/* #4 — Folder delete confirmation. Styled AlertDialog (consistent
          with the document-delete confirm inside LibraryDocumentsView).
          Replaces the native browser confirm the sidebar used before. */}
      <AlertDialog
        open={pendingFolderDelete !== null}
        onOpenChange={(open) => { if (!open && !folderDeleteBusy) setPendingFolderDelete(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete folder &ldquo;{pendingFolderDelete?.name}&rdquo;?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Documents inside this folder will be moved to the root level. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={folderDeleteBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-folder-delete"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={folderDeleteBusy}
              onClick={async (e) => {
                // Prevent Radix from auto-closing before the async DELETE
                // resolves so the busy state stays visible.
                e.preventDefault();
                const target = pendingFolderDelete;
                if (!target) return;
                setFolderDeleteBusy(true);
                try {
                  await apiClient.delete(`/wiki/folders/${target.id}`);
                  // L18-04 — the server moved everything inside (documents,
                  // articles, sub-folders) to the root in one transaction;
                  // every list re-asks it, so the screen shows them there,
                  // not under a folder id that no longer exists.
                  await refreshLists();
                  // If the deleted folder was the active selection, drop back
                  // to the unfiltered list so the user isn't stranded on a
                  // folder that no longer exists.
                  if (activeFolder?.id === target.id) {
                    setActiveFolder(null);
                    setViewMode('list');
                  }
                  toast.success('Folder deleted');
                  setPendingFolderDelete(null);
                } catch (err) {
                  toast.error(getApiErrorMessage(err, 'Failed to delete folder'));
                } finally {
                  setFolderDeleteBusy(false);
                }
              }}
            >
              {folderDeleteBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : null}
              Delete folder
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share dialogs (#7 row item triggers shareDoc; #8 folder
          triggers from KbSidebar.onShareFolder) */}
      {shareDoc && (
        <ShareDocumentDialog
          open={shareDoc !== null}
          onOpenChange={(o) => { if (!o) setShareDoc(null); }}
          documentId={shareDoc.id}
          documentTitle={shareDoc.title}
        />
      )}
      {shareFolder && (
        <ShareFolderDialog
          open={shareFolder !== null}
          onOpenChange={(o) => { if (!o) setShareFolder(null); }}
          folderId={shareFolder.id}
          folderName={shareFolder.name}
        />
      )}

      {/* Move-to-folder dialog (#9) — fired by adapter onBulkMove or
          onRowMove. Route owns the dialog state. */}
      {moveToFolder && (
        <Dialog
          open={moveToFolder !== null}
          onOpenChange={(o) => { if (!o) closeMoveDialog(); }}
        >
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {moveToFolder.targetIds.length === 1
                  ? `Move "${moveToFolder.title ?? 'document'}"`
                  : `Move ${moveToFolder.targetIds.length} documents`}
              </DialogTitle>
              <DialogDescription>
                Pick a destination folder. Wiki articles in your selection are skipped (move them via the editor sidebar instead).
              </DialogDescription>
            </DialogHeader>
            <div className="py-2 space-y-3">
              <Select
                value={moveToFolderId ?? '__root__'}
                onValueChange={(v) => setMoveToFolderId(v === '__root__' ? null : v)}
              >
                <SelectTrigger aria-label="Destination folder">
                  <SelectValue placeholder="Select folder" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__root__">
                    <span className="inline-flex items-center gap-1.5">
                      <FolderIcon className="h-3.5 w-3.5" />
                      All Documents (root)
                    </span>
                  </SelectItem>
                  {folders.map(f => (
                    <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {foldersQ.isError && !foldersQ.data ? (
                <FolderListError
                  consequence="Only the root can be chosen until it loads."
                  onRetry={() => void foldersQ.refetch()}
                />
              ) : folders.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No folders yet. Create one from the New menu first, or move to root.
                </p>
              ) : null}
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={closeMoveDialog}
                disabled={bulkBusy}
                title="Cancel move"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmMove}
                disabled={bulkBusy}
                title="Confirm move"
              >
                {bulkBusy
                  ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                  : <Send className="h-3.5 w-3.5 mr-2" />}
                Move
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
